import React, {useContext} from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import {CircularProgress, InputAdornment} from "@mui/material";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {NotificationType} from "../../../constants/global";
import {addUserValidationSchema} from "../../../constants/validationSchemas";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {useMutationRegisterUser} from "../../../queries/user/hooks/useMutationRegisterUser";
import {PharmacyContext} from "../../../hooks/pharmacyContext";
import {ToasterContext} from "../../../components/shared/Toaster/ToasterContext";
import {useNavigate} from "react-router-dom";
import {routeConfigs} from "../../../navigation/routeConfig";
import {PHARMACY_USERS} from "../../../queries/pharmacy/pharmacyTypeDefs";

export const AddUser:React.FunctionComponent = () => {
  const {addToast} = useContext(ToasterContext);
  const navigate  = useNavigate();
  const { pharmacyId }: any = useContext(PharmacyContext);
  const [registerUser, { loading }] = useMutationRegisterUser({
    refetchQueries: [
      {
        query: PHARMACY_USERS,
        variables: {
          where: { Pharmacy: { is: { id: { equals: +pharmacyId } } } },
          orderBy: [ { createdAt: "asc" } ]
        },
        awaitRefetchQueries: true,
      },
    ],
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(addUserValidationSchema)
  });

  const onSubmit = async (data: any) => {
    const requestData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: `+1${data.phone}`,
      pharmacyId: `${pharmacyId}`,
    }
    registerUser({ variables: { data: requestData } }).then(() => {
      addToast({ type: NotificationType.Success, title: 'User created successfully' });
      navigate(routeConfigs.users.path);
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  };

  return (
    <Container component="main" maxWidth="md" sx={{mb: 4}}>
      <Paper sx={{p: 4}}>
        <Box sx={{ pb: 3 }}>
          <Typography component="h4" variant="h6">Add New User</Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{mt: 2}}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="First Name"
                id="firstName"
                fullWidth
                required
                helperText={errors.firstName?.message?.toString()}
                {...register('firstName')}
                error={!!errors.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoComplete="family-name"
                label="Last Name"
                id="lastName"
                fullWidth
                required
                helperText={errors.lastName?.message?.toString()}
                {...register('lastName')}
                error={!!errors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoComplete="email"
                label="Email Address"
                id="email"
                fullWidth
                required
                {...register('email')}
                helperText={errors.email?.message?.toString()}
                error={!!errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoComplete="phone"
                label="Phone number"
                id="phone"
                fullWidth
                required
                helperText={errors.phone?.message?.toString()}
                {...register('phone')}
                error={!!errors.phone}
                InputProps={{
                  startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{mt: 3, mb: 2}}
          >
            Add New User
            { loading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default AddUser;